import * as React from 'react'
import { Calendar as CalendarIcon, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { formatDateLabelStable, todayLocalDateString } from 'helpers/dateTime'
import { normalizeAppLanguage } from 'helpers/documentLocale'
import { cn } from '../lib/utils'
import { Button } from './ui/button'
import { Calendar } from './ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover'
import { calendarDateToYmd, parseCalendarDate } from './date-picker'

export type LocalDateTimeValue = string | null

type Props = {
 id?: string
 value: LocalDateTimeValue
 onChange: (value: LocalDateTimeValue) => void
 placeholder?: string
 disabled?: boolean
 clearable?: boolean
 min?: string
 max?: string
 error?: string
 className?: string
 'aria-label'?: string
 'aria-invalid'?: boolean
 'aria-describedby'?: string
}

function splitLocalDateTime(value: string | null, fallbackTime = '00:00'): { date: string; time: string } {
 if (!value) return { date: '', time: fallbackTime }
 const match = /^(\d{4}-\d{2}-\d{2})(?:[T ](\d{2}:\d{2}))?/.exec(value.trim())
 return match ? { date: match[1], time: match[2] || fallbackTime } : { date: '', time: fallbackTime }
}

function formatLocalDateTime(value: string | null): string {
 if (!value) return ''
 const parsed = splitLocalDateTime(value)
 return parsed.date ? `${formatDateLabelStable(parsed.date)} ${parsed.time}` : ''
}

function withinBounds(value: string, min?: string, max?: string): boolean {
 return (!min || value >= min) && (!max || value <= max)
}

export function DateTimePicker({
 id,
 value,
 onChange,
 placeholder,
 disabled,
 clearable = true,
 min,
 max,
 error,
 className,
 'aria-label': ariaLabel,
 'aria-invalid': ariaInvalid,
 'aria-describedby': ariaDescribedBy,
}: Props) {
 const { t, i18n } = useTranslation()
 const lang = normalizeAppLanguage(i18n.language)
 const rtl = lang === 'ar'
 const [open, setOpen] = React.useState(false)
 const [draftDate, setDraftDate] = React.useState(() => splitLocalDateTime(value).date)
 const [draftTime, setDraftTime] = React.useState(() => splitLocalDateTime(value).time)
 const [month, setMonth] = React.useState<Date>(() =>
  parseCalendarDate(splitLocalDateTime(value).date || todayLocalDateString()) ?? new Date(),
 )
 const displayed = formatLocalDateTime(value)
 const errorDescriptionId = error ? `${id || 'datetime-picker'}-error` : undefined
 const describedBy = [ariaDescribedBy, errorDescriptionId].filter(Boolean).join(' ') || undefined
 const draftValue = draftDate ? `${draftDate}T${draftTime}` : ''
 const boundsValid = !draftValue || withinBounds(draftValue, min, max)
 const validDateTime = Boolean(draftDate && /^([01]\d|2[0-3]):[0-5]\d$/.test(draftTime) && boundsValid)

 React.useEffect(() => {
  if (!open) {
   const next = splitLocalDateTime(value)
   setDraftDate(next.date)
   setDraftTime(next.time)
   setMonth(parseCalendarDate(next.date || todayLocalDateString()) ?? new Date())
  }
 }, [open, value])

 const calendarDisabled = (date: Date) => {
  const ymd = calendarDateToYmd(date)
  const minDate = min?.slice(0, 10)
  const maxDate = max?.slice(0, 10)
  return Boolean((minDate && ymd < minDate) || (maxDate && ymd > maxDate))
 }

 return (
  <div className="relative min-w-0" dir={rtl ? 'rtl' : 'ltr'}>
   <Popover
    open={open}
    onOpenChange={(next) => {
     if (next) {
      const initial = splitLocalDateTime(value)
      setDraftDate(initial.date)
      setDraftTime(initial.time)
      setMonth(parseCalendarDate(initial.date || todayLocalDateString()) ?? new Date())
     }
     setOpen(next)
    }}
   >
    <PopoverTrigger asChild>
     <button
      id={id}
      type="button"
      disabled={disabled}
      aria-label={ariaLabel ?? (displayed || placeholder || t('ui.dateTime.placeholder'))}
      aria-expanded={open}
      aria-describedby={describedBy}
      className={cn(
       'flex h-10 min-h-10 w-full items-center rounded-xl border border-border-subtle bg-background px-3 py-2 text-sm shadow-sm',
       'text-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0',
       clearable && value && 'pe-10',
       disabled && 'cursor-not-allowed opacity-60',
       (ariaInvalid || error) && 'border-destructive focus-visible:ring-destructive',
       className,
      )}
     >
      <CalendarIcon className="me-2 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      <span dir="ltr" className={cn('min-w-0 flex-1 truncate tabular-nums [unicode-bidi:isolate]', !displayed && 'text-muted-foreground')}>
       {displayed || placeholder || t('ui.dateTime.placeholder')}
      </span>
     </button>
    </PopoverTrigger>
    <PopoverContent align="start" sideOffset={4} className="z-[110000] w-auto max-w-[calc(100vw-1rem)] p-0" dir={rtl ? 'rtl' : 'ltr'}>
     <div className="max-h-[min(80vh,42rem)] overflow-y-auto p-2">
      <Calendar
       mode="single"
       selected={draftDate ? parseCalendarDate(draftDate) : undefined}
       onSelect={(date) => {
        if (date) setDraftDate(calendarDateToYmd(date))
       }}
       month={month}
       onMonthChange={setMonth}
       disabled={calendarDisabled}
       dir={rtl ? 'rtl' : 'ltr'}
       autoFocus
      />
      <div className="border-t border-border-subtle px-2 pb-2 pt-3">
       <label className="flex items-center justify-between gap-4">
        <span className="text-sm font-medium text-foreground">{t('ui.dateTime.time')}</span>
        <input
         type="time"
         step={60}
         lang="en-GB"
         dir="ltr"
         value={draftTime}
         onChange={(event) => setDraftTime(event.target.value)}
         disabled={!draftDate}
         aria-label={t('ui.dateTime.time')}
         className="h-9 w-32 rounded-lg border border-border-subtle bg-background px-2 text-sm tabular-nums shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
       </label>
       {!boundsValid ? <p role="alert" className="mt-2 text-xs text-destructive">{t('ui.dateTime.outOfBounds')}</p> : null}
       <div className="mt-3 flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>
         {t('ui.dateTime.cancel')}
        </Button>
        <Button
         type="button"
         size="sm"
         disabled={!validDateTime}
         onClick={() => {
          onChange(draftValue)
          setOpen(false)
         }}
        >
         {t('ui.dateTime.apply')}
        </Button>
       </div>
      </div>
     </div>
    </PopoverContent>
   </Popover>
   {clearable && value && !disabled ? (
    <button
     type="button"
     className="absolute end-2 top-1/2 z-10 -translate-y-1/2 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
     onClick={() => onChange(null)}
     aria-label={t('pos.datePicker.clear')}
    >
     <X className="h-4 w-4" />
    </button>
   ) : null}
   {error ? <p id={errorDescriptionId} role="alert" className="mt-1 text-xs text-destructive">{error}</p> : null}
  </div>
 )
}
